import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  IApiPackaging,
  IPackagingSearchParams,
  IPackagingListResponse,
  IPackagingResponse,
  IPackagingCreateUpdate
} from '../interfaces/packaging.api.interface';
import { DateHelper } from 'src/helpers/DateHelper';

@Injectable({
  providedIn: 'root'
})
export class PackagingApiService {

  private readonly apiUrl = `${environment.api}/packagings`;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene la lista de packagings con filtros
   * @param searchParams Parámetros de búsqueda
   * @returns Observable con la lista de packagings y el total
   */
  public getPackagingsList(searchParams: IPackagingSearchParams): Observable<IPackagingListResponse> {
    let params = new HttpParams();
    if (searchParams.currentPage !== undefined) {
      params = params.append('currentPage', searchParams.currentPage.toString());
    }
    if (searchParams.elementsPerPage !== undefined) {
      params = params.append('elementsPerPage', searchParams.elementsPerPage.toString());
    }
    if (searchParams.idCompany) {
      params = params.append('idCompany', searchParams.idCompany.toString());
    }
    if (searchParams.idClientLocation) {
      params = params.append('idClientLocation', searchParams.idClientLocation.toString());

    }
    if (searchParams.idCenter) {
      params = params.append('idCenter', searchParams.idCenter.toString());
    }
    if (searchParams.idArea) {
      params = params.append('idArea', searchParams.idArea.toString());
    }
    if (searchParams.qrDataSearch) {
      params = params.append('qrDataSearch', searchParams.qrDataSearch);
    }
    if (searchParams.ean) {
      params = params.append('ean', searchParams.ean);
    }
    if (searchParams.waste) {
      params = params.append('waste', searchParams.waste);
    }
    if (searchParams.type) {
      params = params.append('type', searchParams.type);
    }
    if (searchParams.status !== undefined && searchParams.status !== null) {
      params = params.append('status', searchParams.status.toString());
    }
    if (searchParams.dateType) {
      params = params.append('dateType', searchParams.dateType);
    }
    if (searchParams.dateFrom) {
      const dateFrom = typeof searchParams.dateFrom === 'string'
        ? searchParams.dateFrom
        : DateHelper.toLocalISOString(searchParams.dateFrom);
      params = params.append('dateFrom', dateFrom);
    }
    if (searchParams.dateTo) {
      const dateTo = typeof searchParams.dateTo === 'string'
        ? searchParams.dateTo
        : DateHelper.toLocalISOString(searchParams.dateTo);
      params = params.append('dateTo', dateTo);
    }

    return this.http.get<IPackagingListResponse>(`${this.apiUrl}/list`, { params });
  }

  /**
   * Obtiene un packaging por su ID
   * @param idPackaging ID del packaging
   * @returns Observable con los datos del packaging
   */
  public getPackagingById(idPackaging: number): Observable<{ results: IApiPackaging }> {
    const params = new HttpParams().set('idPackaging', idPackaging.toString());
    return this.http.get<{ results: IApiPackaging }>(`${this.apiUrl}/getById`, { params });
  }

  /**
   * Crea un nuevo packaging
   * @param packaging Datos del packaging a crear
   * @returns Observable con la respuesta de la creación
   */
  public createPackaging(packaging: IPackagingCreateUpdate): Observable<IPackagingResponse> {
    return this.http.put<IPackagingResponse>(`${this.apiUrl}/add`, packaging);
  }

  /**
   * Actualiza un packaging existente
   * @param packaging Datos del packaging a actualizar
   * @returns Observable con la respuesta de la actualización
   */
  public updatePackaging(packaging: IPackagingCreateUpdate): Observable<IPackagingResponse> {
    return this.http.post<IPackagingResponse>(`${this.apiUrl}/edit`, packaging);
  }

  /**
   * Obtiene estadísticas de conteo de packagings por estado
   * @param idCenter ID del centro (opcional)
   * @param idArea ID del área (opcional)
   * @param dateFrom Fecha desde para filtrar estado "Recibido" (opcional)
   * @param dateTo Fecha hasta para filtrar estado "Recibido" (opcional)
   * @returns Observable con array de estadísticas por estado
   */
  public getPackagingStatistics(
    idCenter?: number,
    idArea?: number,
    dateFrom?: Date,
    dateTo?: Date
  ): Observable<{ results: Array<{ status: number; count: number }> }> {
    let params = new HttpParams();

    if (idCenter) {
      params = params.append('idCenter', idCenter.toString());
    }
    if (idArea) {
      params = params.append('idArea', idArea.toString());
    }
    if (dateFrom) {
      const dateFromStr = DateHelper.toLocalISOString(dateFrom);
      params = params.append('dateFrom', dateFromStr);
    }
    if (dateTo) {
      const dateToStr = DateHelper.toLocalISOString(dateTo);
      params = params.append('dateTo', dateToStr);
    }

    return this.http.get<{ results: Array<{ status: number; count: number }> }>(
      `${this.apiUrl}/statistics`,
      { params }
    );
  }

  /**
   * Exporta packagings a CSV
   * @param searchParams Parámetros de búsqueda
   * @returns Observable con el Blob del archivo CSV
   */
  public exportPackagingsToCSV(searchParams: IPackagingSearchParams): Observable<Blob> {
    let params = new HttpParams();
    
    if (searchParams.currentPage !== undefined) {
      params = params.append('currentPage', searchParams.currentPage.toString());
    }
    if (searchParams.elementsPerPage !== undefined) {
      params = params.append('elementsPerPage', searchParams.elementsPerPage.toString());
    }
    if (searchParams.idCompany) {
      params = params.append('idCompany', searchParams.idCompany.toString());
    }
    if (searchParams.idClientLocation) {
      params = params.append('idClientLocation', searchParams.idClientLocation.toString());
    }
    if (searchParams.idCenter) {
      params = params.append('idCenter', searchParams.idCenter.toString());
    }
    if (searchParams.idArea) {
      params = params.append('idArea', searchParams.idArea.toString());
    }
    if (searchParams.qrDataSearch) {
      params = params.append('qrDataSearch', searchParams.qrDataSearch);
    }
    if (searchParams.ean) {
      params = params.append('ean', searchParams.ean);
    }
    if (searchParams.waste) {
      params = params.append('waste', searchParams.waste);
    }
    if (searchParams.type) {
      params = params.append('type', searchParams.type);
    }
    if (searchParams.status !== undefined && searchParams.status !== null) {
      params = params.append('status', searchParams.status.toString());
    }
    if (searchParams.dateType) {
      params = params.append('dateType', searchParams.dateType);
    }
    if (searchParams.dateFrom) {
      const dateFrom = typeof searchParams.dateFrom === 'string'
        ? searchParams.dateFrom
        : DateHelper.toLocalISOString(searchParams.dateFrom);
      params = params.append('dateFrom', dateFrom);
    }
    if (searchParams.dateTo) {
      const dateTo = typeof searchParams.dateTo === 'string'
        ? searchParams.dateTo
        : DateHelper.toLocalISOString(searchParams.dateTo);
      params = params.append('dateTo', dateTo);
    }

    return this.http.get(`${this.apiUrl}/export`, { 
      params,
      responseType: 'blob'
    });
  }
}

